import { Component, HostListener, ViewChild } from '@angular/core';

import {
	AccountModel,
	AccountService,
	AuthenticatorStatus,
	AppSource,
	AuthService,
	BaseComponent,
	EnableEmailAuthenticatorModel,
	EnableMobileAuthenticatorModel,
	Enviroment,
	ImpersonateModel,
	LayoutService,
	ResetEmailAuthenticator,
	SearchResult
} from '@nstep-common/core';

import { ModalComponent } from '@nstep-common/semantic-ui';
import { flatten, map } from 'lodash';
import { catchError, EMPTY, finalize, Observable, tap } from 'rxjs';

@Component({
	selector: 'app-base-layout',
	styleUrl: './base-layout.component.css',
	templateUrl: './base-layout.component.html',
	standalone: false
})
export class BaseLayoutComponent extends BaseComponent {

	@ViewChild('changePasswordModal') changePasswordModal!: ModalComponent;
	@ViewChild('twoFactorModal') twoFactorModal!: ModalComponent;

	protected errors: string[] = [];
	protected impersonateErrors: string[] = [];

	protected authenticator: AuthenticatorStatus | null = null;
	protected showMobileAuth = true;
	protected newEmail: string | null = null;
	protected askForCode: boolean = false;
	protected code: string[] = new Array(6).fill('');
	protected isTwoFactorLoading = false;

	protected isMobile = true;
	protected isTablet = true;
	protected isSmallMonitor = true;
	protected isRegularMonitor = true;
	protected isLargeMonitor = true;

	protected currentUser: string | null = null;
	protected isAdministrator = false;
	protected isImpersonating = false;
	protected isImpersonated = false;
	protected hasTotalAccount = false;

	protected appVersion = 'N/A';

	constructor(private layoutService: LayoutService,
		private authService: AuthService,
		private accountService: AccountService,
		private environment: Enviroment) {
		super();

		this.appVersion = `v${this.environment.appVersion}`;

		this.currentUser = this.authService.jwt?.name ?? 'N/A';
		const impersonationPermission = this.environment.appName === AppSource.Public
			? 'F30DE9A2'
			: 'E30DE9A2';
		this.isAdministrator = this.authService.hasAccess(impersonationPermission);
		this.isImpersonated = !!this.authService.jwt?.impersonatedBy;
		this.hasTotalAccount = this.authService.jwt?.hasTotalAccount != null && Boolean(this.authService.jwt.hasTotalAccount);

		// This is necessary when redirecting from successfull login to components using this layout
		this.updateLayout(window.innerWidth);

		this.getAuthStatus();
	}

	@HostListener('window:load', ['$event'])
	@HostListener('window:resize', ['$event'])
	onResize(event: Event) {
		const width = (event.currentTarget as Window).innerWidth;
		this.updateLayout(width);
	}

	private updateLayout(width: number): void {
		this.isMobile = width < 768;
		this.isTablet = width >= 768 && width <= 991;
		this.isSmallMonitor = width >= 992 && width <= 1920;
		this.isRegularMonitor = width > 1920 && width <= 2560;
		this.isLargeMonitor = width > 2560;

		this.layoutService.layoutHasChanged.next({
			isMobile: this.isMobile,
			isTablet: this.isTablet,
			isSmallMonitor: this.isSmallMonitor,
			isRegularMonitor: this.isRegularMonitor,
			isLargeMonitor: this.isLargeMonitor
		});
	}

	logout(): void {
		this.authService.logOut();
	}

	searchUserResult: SearchResult<ImpersonateModel> | null = null;
	searchUser: (term: string, callback: (results: SearchResult<ImpersonateModel>[]) => void) => void = (term, callback) => {
		const getImpersonables$ = this.accountService.impersonables(term)
			.subscribe(resp => {
				const results = map(resp, item => ({
					obj: item,
					description: '',
					title: item.userName
				}));

				callback(results);
			});

		this.subscriptions.push(getImpersonables$);
	};

	impersonateUser(): void {
		this.isImpersonating = true;

		const impersonateUser$ = this.accountService.impersonate(this.searchUserResult?.obj!)
		    .pipe(
				catchError((resp: any) => {
					const errors = flatten(Object.values(resp)) as string[];
					this.impersonateErrors = errors;

					return EMPTY;
				}),
				finalize(() => {
					this.isImpersonating = false;
				})
			)
			.subscribe({
				next: (model: AccountModel) => {
					this.authService.logIn(model, true);
					window.location.href = '';
				},
				error: () => {
					this.searchUserResult = null;
				}
			});

		this.subscriptions.push(impersonateUser$);
	}

	getAuthStatus(): void {
		this.isTwoFactorLoading = true;

		const getTwoFactor$ = this.accountService.getTwoFactor()
			.pipe(
				tap(m => this.authenticator = m),
				finalize(() => this.isTwoFactorLoading = false)
			)
			.subscribe();

		this.subscriptions.push(getTwoFactor$);
	}


	resetAuthenticator(): void {
		this.subscriptions.push(this.accountService.resetMobileAuthenticator()
			.subscribe(r => {
				this.authenticator!.mobile = r;
			}));
	}

	removeEmail(email: string): void {
		if (!this.authenticator) {
			return;
		}

		this.isTwoFactorLoading = true;

		var sub$ = this.accountService
			.resetEmailAuthenticator(new ResetEmailAuthenticator({
				email: email
			}))
			.pipe(
				tap(() => this.getAuthStatus()),
				finalize(() => this.isTwoFactorLoading = false)
			)
			.subscribe();

		this.subscriptions.push(sub$);
	}

	handleInput(event: any, index: number): void {
		const value = event.target.value;

		if (/^\d$/.test(value)) {
			if (index < 5) {
				document.getElementById(`digit-${index + 1}`)?.focus();
			}
		}
	}

	handleKeyDown(event: any, index: number): void {
		if (event.key === 'Backspace' && !this.code[index] && index > 0) {
			document.getElementById(`digit-${index - 1}`)?.focus();
		}
	}

	handlePaste(event: ClipboardEvent): void {
		event.preventDefault();
		const pasteData = event.clipboardData?.getData('text') || '';
		const digits = pasteData.replace(/\D/g, '').slice(0, 6).split('');

		digits.forEach((digit, i) => {
			if (i < 6) {
				this.code[i] = digit;
				const inputElement = document.getElementById(`digit-${i}`) as HTMLInputElement;
				if (inputElement) {
					inputElement.value = digit;
				}
			}
		});

		const nextEmptyIndex = digits.length < 6 ? digits.length : 5;
		document.getElementById(`digit-${nextEmptyIndex}`)?.focus();
	}

	save2F(): void {
		this.isTwoFactorLoading = true;

		if (this.showMobileAuth) {
			if (!this.askForCode) {
				this.askForCode = true;
				this.isTwoFactorLoading = false;
			}
			else {
				this.handleResponse(this.accountService
					.enableMobileAuthenticator(new EnableMobileAuthenticatorModel({
						code: this.code.join('')
					})), () => {
						this.getAuthStatus();
						this.cancel2F();
					});
			}
		}
		else {
			if (!this.askForCode) {
				this.handleResponse(this.accountService
					.enableEmailAuthenticator(new EnableEmailAuthenticatorModel({
						email: this.newEmail!
					})), () => {
						this.askForCode = true;
					});
			}
			else {
				this.handleResponse(this.accountService
					.enableEmailAuthenticator(new EnableEmailAuthenticatorModel({
						email: this.newEmail!,
						twoFactorCode: this.code.join('')
					})), () => {
						this.getAuthStatus();
						this.cancel2F();
					});
			}
		}
	}

	cancel2F(): void {
		this.twoFactorModal.toggle();
		setTimeout(() => {
			this.isTwoFactorLoading = false;

			this.newEmail = null;

			this.askForCode = false;
			this.code = [];

			this.errors = [];
		}, 200);
	}

	private handleResponse<T>(obs: Observable<T>,
		nextHandler?: ((value: T) => void) | null,
		errorHandler?: ((error: any) => Observable<T>) | null,
		finalizeHandler?: (() => void) | null): void {
		const sub$ = obs
			.pipe(
				tap((resp) => nextHandler && nextHandler(resp)),
				catchError((resp: any) => {
					if (errorHandler) {
						return errorHandler(resp);
					}

					const errors = flatten(Object.values(resp)) as string[];
					this.errors = errors;

					return EMPTY;
				}),
				finalize(() => {
					if (finalizeHandler) {
						finalizeHandler();
						return;
					}

					this.isTwoFactorLoading = false;
				})
			)
			.subscribe();

		this.subscriptions.push(sub$);
	}
}
